import type { State } from '@eformulieren/engine'
import type { Section } from '@eformulieren/model'
import { useCallback, useMemo } from 'react'
import type * as Y from 'yjs'
import { useSnapshot } from './snapshot.js'
import { type FieldInDocument, stateOf } from './state.js'

/**
 * De stand over alle sectiedocumenten samen, meelopend met elke wijziging.
 *
 * De evaluator is een pure functie over de stand (ontwerp 5.5), dus wie de status
 * live wil hebben (R-24) moet de stand live hebben. Deze hook abonneert zich op elk
 * document; een wijziging van een medeschrijver laat de status dus net zo bewegen als
 * een eigen toetsaanslag.
 *
 * Herberekenen bij elke update is hier goedkoop genoeg: de stand is een map van
 * waarden en de duurste stap is het uitlezen van de rich-text-fragmenten. Zou dat
 * knellen bij een groot formulier, dan is de meting van M8 de plek om dat te zien in
 * plaats van het hier vooruit te optimaliseren.
 */
export function useLiveState(
  sections: readonly Section[],
  documents: ReadonlyMap<string, Y.Doc>,
): State {
  const places = useMemo<readonly FieldInDocument[]>(() => {
    const out: FieldInDocument[] = []
    for (const section of sections) {
      const doc = documents.get(section.id)
      if (doc === undefined) continue
      for (const field of section.velden) out.push({ veld: field, doc })
    }
    return out
  }, [sections, documents])

  const subscribe = useCallback(
    (redraw: () => void) => {
      const docs = [...documents.values()]
      for (const doc of docs) doc.on('update', redraw)
      return () => {
        for (const doc of docs) doc.off('update', redraw)
      }
    },
    [documents],
  )

  // De stand moet dezelfde verwijzing houden zolang hij inhoudelijk gelijk is; die
  // vergelijking stond hier en staat nu in `useSnapshot`, waar de draadjes en de
  // presence hem ook gebruiken.
  const read = useCallback(() => stateOf(places), [places])

  return useSnapshot(subscribe, read)
}
